import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Save, Trash2, RefreshCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Fallback icon set if the API's allowed_icons field is missing.
const DEFAULT_ICONS = [
  'Users', 'Shield', 'Compass', 'Mountain', 'Map', 'Tent',
  'Heart', 'Star', 'Award', 'Sparkles', 'Route', 'MapPin',
];

const SectionCard = ({ title, description, children }) => (
  <div className="rounded-xl border border-border p-5 space-y-4">
    <div>
      <h3 className="text-lg font-semibold">{title}</h3>
      {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
    </div>
    {children}
  </div>
);

const Field = ({ label, htmlFor, hint, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

/**
 * Super-admin editor for the public Landing page copy. All fields are
 * optional; sending only the changed ones preserves the rest.
 */
export const ContentTab = ({ token }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/content/landing`);
      setContent(res.data);
    } catch (e) {
      toast.error('Failed to load content settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (patch) => setContent((prev) => ({ ...prev, ...patch }));

  const updateFeature = (i, patch) => {
    const features = [...(content.features || [])];
    features[i] = { ...features[i], ...patch };
    update({ features });
  };
  const addFeature = () => update({
    features: [...(content.features || []), { icon: 'Compass', title: '', description: '' }],
  });
  const removeFeature = (i) => update({
    features: (content.features || []).filter((_, idx) => idx !== i),
  });

  const updateTestimonial = (i, patch) => {
    const testimonials = [...(content.testimonials || [])];
    testimonials[i] = { ...testimonials[i], ...patch };
    update({ testimonials });
  };
  const addTestimonial = () => update({
    testimonials: [...(content.testimonials || []), { quote: '', author: '', role: '', avatar_url: '' }],
  });
  const removeTestimonial = (i) => update({
    testimonials: (content.testimonials || []).filter((_, idx) => idx !== i),
  });

  const save = async () => {
    setSaving(true);
    try {
      // Only send editable fields (drop server-computed keys)
      const {
        allowed_icons: _drop1, updated_at: _drop2, content_id: _drop3, ...payload
      } = content;
      await axios.put(`${API}/admin/content/landing`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Landing content saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return (
      <TabsContent value="content" className="space-y-6">
        <div className="text-muted-foreground">Loading content settings…</div>
      </TabsContent>
    );
  }

  const iconOptions = content.allowed_icons || DEFAULT_ICONS;

  return (
    <TabsContent value="content" className="space-y-6" data-testid="content-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Landing Page Content</h2>
          <p className="text-sm text-muted-foreground">
            Edit the copy on your public marketing page. Empty fields fall back to sensible defaults.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={saving} data-testid="content-reload-btn">
            <RefreshCcw className="h-4 w-4 mr-2" />
            Reload
          </Button>
          <Button onClick={save} disabled={saving} data-testid="content-save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {/* Hero */}
      <SectionCard title="Hero" description="Big headline at the top of the landing page.">
        <Field label="Kicker chip" htmlFor="hero_kicker" hint="Small pill above the headline">
          <Input
            id="hero_kicker"
            value={content.hero_kicker || ''}
            onChange={(e) => update({ hero_kicker: e.target.value })}
            data-testid="hero-kicker-input"
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Headline — line 1" htmlFor="hero_title_line1">
            <Input
              id="hero_title_line1"
              value={content.hero_title_line1 || ''}
              onChange={(e) => update({ hero_title_line1: e.target.value })}
              data-testid="hero-title1-input"
            />
          </Field>
          <Field label="Headline — line 2 (red highlight)" htmlFor="hero_title_line2">
            <Input
              id="hero_title_line2"
              value={content.hero_title_line2 || ''}
              onChange={(e) => update({ hero_title_line2: e.target.value })}
              data-testid="hero-title2-input"
            />
          </Field>
        </div>
        <Field label="Subtitle" htmlFor="hero_subtitle">
          <Textarea
            id="hero_subtitle"
            rows={2}
            value={content.hero_subtitle || ''}
            onChange={(e) => update({ hero_subtitle: e.target.value })}
            data-testid="hero-subtitle-input"
          />
        </Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Primary CTA button" htmlFor="hero_cta_primary">
            <Input
              id="hero_cta_primary"
              value={content.hero_cta_primary || ''}
              onChange={(e) => update({ hero_cta_primary: e.target.value })}
              data-testid="hero-cta-primary-input"
            />
          </Field>
        </div>
      </SectionCard>

      {/* Features */}
      <SectionCard title="Feature cards" description={`The "${content.features_heading || 'How It Works'}" section — 1 to 6 cards.`}>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Section heading" htmlFor="features_heading">
            <Input
              id="features_heading"
              value={content.features_heading || ''}
              onChange={(e) => update({ features_heading: e.target.value })}
              data-testid="features-heading-input"
            />
          </Field>
          <Field label="Section subheading" htmlFor="features_subheading">
            <Input
              id="features_subheading"
              value={content.features_subheading || ''}
              onChange={(e) => update({ features_subheading: e.target.value })}
              data-testid="features-subheading-input"
            />
          </Field>
        </div>

        <div className="space-y-3">
          {(content.features || []).map((f, i) => (
            <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`feature-editor-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase">Feature {i + 1}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFeature(i)}
                  className="text-destructive"
                  data-testid={`feature-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Icon</Label>
                  <Select value={f.icon} onValueChange={(v) => updateFeature(i, { icon: v })}>
                    <SelectTrigger data-testid={`feature-icon-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map((ic) => (
                        <SelectItem key={ic} value={ic}>{ic}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs">Title</Label>
                  <Input
                    value={f.title}
                    onChange={(e) => updateFeature(i, { title: e.target.value })}
                    data-testid={`feature-title-${i}`}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  rows={2}
                  value={f.description}
                  onChange={(e) => updateFeature(i, { description: e.target.value })}
                  data-testid={`feature-desc-${i}`}
                />
              </div>
            </div>
          ))}
          {(content.features || []).length < 6 && (
            <Button
              variant="outline"
              onClick={addFeature}
              className="w-full"
              data-testid="feature-add-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Add feature card
            </Button>
          )}
        </div>
      </SectionCard>

      {/* Testimonials */}
      <SectionCard
        title="Testimonials"
        description="Optional social-proof section between features and the bottom CTA. Toggle off to hide entirely."
      >
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
          <Label htmlFor="testimonials_enabled" className="cursor-pointer">
            Show testimonials section on the landing page
          </Label>
          <Switch
            id="testimonials_enabled"
            checked={!!content.testimonials_enabled}
            onCheckedChange={(v) => update({ testimonials_enabled: v })}
            data-testid="testimonials-enabled-toggle"
          />
        </div>

        {content.testimonials_enabled && (
          <>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Section heading" htmlFor="testimonials_heading">
                <Input
                  id="testimonials_heading"
                  value={content.testimonials_heading || ''}
                  onChange={(e) => update({ testimonials_heading: e.target.value })}
                  data-testid="testimonials-heading-input"
                />
              </Field>
              <Field label="Section subheading" htmlFor="testimonials_subheading">
                <Input
                  id="testimonials_subheading"
                  value={content.testimonials_subheading || ''}
                  onChange={(e) => update({ testimonials_subheading: e.target.value })}
                  data-testid="testimonials-subheading-input"
                />
              </Field>
            </div>

            <div className="space-y-3">
              {(content.testimonials || []).map((t, i) => (
                <div key={i} className="rounded-lg border border-border p-3 space-y-2" data-testid={`testimonial-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase">Testimonial {i + 1}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTestimonial(i)}
                      className="text-destructive"
                      data-testid={`testimonial-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    placeholder="Quote"
                    rows={2}
                    value={t.quote}
                    onChange={(e) => updateTestimonial(i, { quote: e.target.value })}
                    data-testid={`testimonial-quote-${i}`}
                  />
                  <div className="grid md:grid-cols-3 gap-2">
                    <Input
                      placeholder="Author name"
                      value={t.author}
                      onChange={(e) => updateTestimonial(i, { author: e.target.value })}
                      data-testid={`testimonial-author-${i}`}
                    />
                    <Input
                      placeholder="Role / location"
                      value={t.role || ''}
                      onChange={(e) => updateTestimonial(i, { role: e.target.value })}
                      data-testid={`testimonial-role-${i}`}
                    />
                    <Input
                      placeholder="Avatar image URL (optional)"
                      value={t.avatar_url || ''}
                      onChange={(e) => updateTestimonial(i, { avatar_url: e.target.value })}
                      data-testid={`testimonial-avatar-${i}`}
                    />
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addTestimonial}
                className="w-full"
                data-testid="testimonial-add-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> Add testimonial
              </Button>
            </div>
          </>
        )}
      </SectionCard>

      {/* Bottom CTA */}
      <SectionCard title="Bottom CTA banner" description="The final call-to-action above the footer.">
        <Field label="Heading" htmlFor="bottom_cta_heading">
          <Input
            id="bottom_cta_heading"
            value={content.bottom_cta_heading || ''}
            onChange={(e) => update({ bottom_cta_heading: e.target.value })}
            data-testid="bottom-cta-heading-input"
          />
        </Field>
        <Field label="Subheading" htmlFor="bottom_cta_subheading">
          <Input
            id="bottom_cta_subheading"
            value={content.bottom_cta_subheading || ''}
            onChange={(e) => update({ bottom_cta_subheading: e.target.value })}
            data-testid="bottom-cta-subheading-input"
          />
        </Field>
        <Field label="Button text" htmlFor="bottom_cta_button">
          <Input
            id="bottom_cta_button"
            value={content.bottom_cta_button || ''}
            onChange={(e) => update({ bottom_cta_button: e.target.value })}
            data-testid="bottom-cta-button-input"
          />
        </Field>
      </SectionCard>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} size="lg" data-testid="content-save-btn-bottom">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </TabsContent>
  );
};

export default ContentTab;
